import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertCircle, Compass, Trophy } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { PrimaryButton, GhostButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";
import { portfolioCategories, featuredCaseStudy } from "../data/site";

export default function CaseStudy() {
  const { slug } = useParams();
  const all = useMemo(() => [...portfolioCategories, featuredCaseStudy], []);
  const item = useMemo(() => all.find((p) => p.slug === slug), [all, slug]);

  if (!item) return <Navigate to="/" replace />;

  const idx = all.findIndex((p) => p.slug === slug);
  const next = all[(idx + 1) % all.length];

  return (
    <main data-testid={`case-study-${slug}`}>
      <PageHead
        title={`${item.label} Case Study | Digital Archdeacon Media Ltd`}
        description={item.case_study.tagline}
      />

      {/* Hero — uses the portfolio image as the hero */}
      <section className="relative pt-32 pb-12 overflow-hidden" style={heroBgStyle}>
        <ParticleField density={40} />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="container-x relative">
          <Link
            to="/#portfolio-teaser"
            className="inline-flex items-center gap-2 text-sm text-[#CBD5E1] hover:text-white mb-8"
            data-testid="case-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            All case studies
          </Link>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <Reveal>
              <div>
                <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Case study</span>
                <h1 className="h-display text-[clamp(2.5rem,6vw,5rem)] mt-4">
                  {item.label}
                </h1>
                <p className="mt-5 text-lg sm:text-xl text-[#CBD5E1] leading-relaxed max-w-xl">
                  {item.case_study.tagline}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <PrimaryButton to="/request-quote" testId="case-cta-quote">
                    Get a Quote Like This
                  </PrimaryButton>
                  <GhostButton to="/contact" testId="case-cta-contact">
                    Discuss Your Project
                  </GhostButton>
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative aspect-[4/3] rounded-[28px] overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(30,111,255,0.18)]">
                <img
                  src={item.image}
                  alt={item.label}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F2C] via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Problem — light blue */}
      <section className="section-pad section-light-blue" data-testid="case-problem">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4">
            <div className="inline-flex items-center gap-3 rounded-full border border-electric/30 bg-electric/10 pl-2 pr-4 py-1.5">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-electric/20 text-electric">
                <AlertCircle className="h-4 w-4" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-electric font-semibold">
                The Problem
              </span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl text-[#0A0F2C] mt-5">
              Where the work began.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={120}>
            <p className="text-lg text-[#1E293B] leading-relaxed">{item.case_study.problem}</p>
          </Reveal>
        </div>
      </section>

      {/* Approach — dark */}
      <section className="section-pad" data-testid="case-approach">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4">
            <div className="inline-flex items-center gap-3 rounded-full border border-gold/30 bg-gold/10 pl-2 pr-4 py-1.5">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-gold/20 text-gold">
                <Compass className="h-4 w-4" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">
                The Approach
              </span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl mt-5">How we worked.</h2>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={120}>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">{item.case_study.approach}</p>
          </Reveal>
        </div>
      </section>

      {/* Result — light white */}
      <section className="section-pad section-white" data-testid="case-result">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4">
            <div className="inline-flex items-center gap-3 rounded-full border border-electric/30 bg-electric/10 pl-2 pr-4 py-1.5">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-electric/20 text-electric">
                <Trophy className="h-4 w-4" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-electric font-semibold">
                The Result
              </span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl text-[#0A0F2C] mt-5">
              What changed.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={120}>
            <p className="text-lg text-[#1E293B] leading-relaxed">{item.case_study.result}</p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="case-cta-section">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-12 md:p-16">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <div className="absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
              <div className="relative">
                <h2 className="h-display text-4xl md:text-5xl">
                  Want a result like this?
                </h2>
                <p className="mt-5 text-lg text-[#CBD5E1] max-w-2xl">
                  Tell us what you're trying to do. We'll come back in 24 hours with a tailored plan.
                </p>
                <div className="mt-8 flex flex-wrap gap-4 items-center">
                  <PrimaryButton to="/request-quote" testId="case-cta-quote-bottom">
                    Get a Free Quote
                  </PrimaryButton>
                  <Link
                    to={`/case-studies/${next.slug}`}
                    className="inline-flex items-center gap-2 text-white font-semibold hover:text-gold transition-colors"
                    data-testid="case-next-link"
                  >
                    Next case study: {next.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
